import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import type { AccountStoryDocument, WorldDocument } from "@private-fiction/story-core";
import { playerVisibleDocuments } from "@private-fiction/story-core";
import { StoryFileView } from "./StoryFileView";

/** UI identity only: the body always reads the current document revision. */
export function StoryFileWindow({ immersiveGlass = false, themeStyle, documentId, documents, session, onClose, onSelectEntity, active = true, onActivate, offset = 0 }: {
  active?: boolean; onActivate?: () => void; offset?: number;
  immersiveGlass?: boolean; themeStyle?: CSSProperties; documentId: string; documents: readonly WorldDocument[]; session: AccountStoryDocument | null;
  onClose: () => void; onSelectEntity: (document: WorldDocument) => void;
}) {
  const panel = useRef<HTMLElement>(null);
  const titleId = useId();
  const body = useRef<HTMLDivElement>(null);
  // Own the host so closing or route unmount cannot leave a body-level portal behind.
  const [portalHost] = useState(() => window.document.createElement("div"));
  useLayoutEffect(() => { window.document.body.appendChild(portalHost); return () => portalHost.remove(); }, [portalHost]);
  const [dismissed, setDismissed] = useState(false);
  const dismiss = () => { portalHost.remove(); setDismissed(true); onClose(); };
  const close = useRef(dismiss);
  close.current = dismiss;
  const isActive=useRef(active);
  isActive.current=active;
  const [position, setPosition] = useState({ x: Math.max(8, (window.innerWidth - 520) / 2 + offset * 28), y: 80 + offset * 28 });
  const drag = useRef<{ x: number; y: number; left: number; top: number } | null>(null);
  const clamp = (x: number, y: number) => ({
    x: Math.max(8, Math.min(x, window.innerWidth - (panel.current?.offsetWidth ?? 520) - 8)),
    y: Math.max(8, Math.min(y, window.innerHeight - (panel.current?.offsetHeight ?? 400) - 8)),
  });
  useEffect(() => {

    const previous = window.document.activeElement as HTMLElement | null;
    panel.current?.focus();
    const resize = () => setPosition(current => clamp(current.x, current.y));
    const escape = (event: KeyboardEvent) => {
      const focusedDialog=window.document.activeElement?.closest('[role="dialog"],dialog[open]');
      if (event.key === "Escape" && !event.defaultPrevented && isActive.current && (!focusedDialog||focusedDialog===panel.current)) {
        event.preventDefault();event.stopImmediatePropagation(); close.current();
      }
    };
    resize();
    const observer = new ResizeObserver(resize);
    if (panel.current) observer.observe(panel.current);
    window.addEventListener("resize", resize);
    window.addEventListener("keydown", escape, true);

    return () => { observer.disconnect(); window.removeEventListener("resize", resize); window.removeEventListener("keydown", escape, true); if (previous?.isConnected) requestAnimationFrame(() => previous.isConnected && previous.focus()); };
  }, []);
  useEffect(() => { body.current?.scrollTo({ top: 0, left: 0 }); panel.current?.focus(); }, [documentId]);
  useEffect(() => { if (active && !panel.current?.contains(window.document.activeElement)) panel.current?.focus(); }, [active]);
  const visibleDocuments = playerVisibleDocuments(documents);
  const document = visibleDocuments.find(item => item.id === documentId);
  if (dismissed) return null;
  return createPortal(<section ref={panel} role="dialog" aria-labelledby={titleId} tabIndex={-1} onPointerDownCapture={onActivate} onFocusCapture={onActivate} className={`story-file-window ${immersiveGlass ? "scene-glass" : ""}`} style={{ ...themeStyle, "--file-window-layer": active ? 10002 : 10000, left: position.x, top: position.y } as CSSProperties}>
    <header onPointerDown={event => {
      if (event.button !== 0 || (event.target as HTMLElement).closest("button")) return;
      drag.current = { x: event.clientX, y: event.clientY, left: position.x, top: position.y };
      panel.current?.focus();
      event.preventDefault(); event.currentTarget.setPointerCapture(event.pointerId);
    }} onPointerMove={event => {
      if (drag.current) setPosition(clamp(drag.current.left + event.clientX - drag.current.x, drag.current.top + event.clientY - drag.current.y));
    }} onPointerUp={event => { drag.current = null; if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId); }} onPointerCancel={() => { drag.current = null; }} onLostPointerCapture={() => { drag.current = null; }}>
      <div><small>รายละเอียดไฟล์</small><h2 id={titleId}>{document?.title ?? "ไม่พบไฟล์นี้แล้ว"}</h2></div>
      <button type="button" aria-label="ปิดรายละเอียดไฟล์" onPointerDown={event => { event.preventDefault(); event.stopPropagation(); dismiss(); }} onPointerUp={event => { event.stopPropagation(); dismiss(); }} onClick={event => { event.stopPropagation(); dismiss(); }}>×</button>
    </header>
    <div ref={body} className="story-file-window__body">{document ? <StoryFileView key={document.id} document={document} documents={visibleDocuments} session={session} onSelectEntity={onSelectEntity} /> : <p>ไฟล์อาจถูกลบหรือไม่อยู่ในเส้นทางนี้</p>}</div>
  </section>, portalHost);
}


